export const getMoodColor = (x: number, y: number): string => {
    // x: 0-7 (Energy: Low -> High)
    // y: 0-7 (Pleasantness: Low -> High)

    // Normalize to 0-1
    const energy = x / 7;
    const pleasantness = y / 7;

    // Bilinear interpolation or simplified quadrant mapping
    // Bottom-Left (0,0): Blue (Alone/Down)
    // Top-Left (0,7): Red (Enraged/Livid) - Wait, Y is Pleasantness?
    // User said: x-Axis measures Energy and the Y-axis measures Pleasantness
    // Let's re-read: "ranges from 'alone' in the bottom left corner to 'ecstatic' in the top left corner"
    // Wait, "ecstatic" is usually High Energy, High Pleasantness.
    // "Alone" is Low Energy, Low Pleasantness.
    // If Bottom-Left is Alone (Low E, Low P)
    // And Top-Left is Ecstatic? That implies Top is High Energy?
    // Let's look at the image again or the text.
    // Text: "x-Axis measures Energy and the Y-axis measures Pleasantness"
    // Image: Y-axis label is "ENERGY" (pointing up), X-axis label is "PLEASANTNESS" (pointing right).
    // So Y = Energy, X = Pleasantness.

    // Let's correct the logic based on the image:
    // Y-Axis (Vertical): Energy (Low at bottom, High at top)
    // X-Axis (Horizontal): Pleasantness (Low at left, High at right)

    // Bottom-Left (0,0): Low Energy, Low Pleasantness -> Blue/Sad
    // Top-Left (0,7): High Energy, Low Pleasantness -> Red/Angry
    // Top-Right (7,7): High Energy, High Pleasantness -> Yellow/Ecstatic
    // Bottom-Right (7,0): Low Energy, High Pleasantness -> Green/Calm

    // We can use HSL or RGB interpolation.
    // Let's try a simple RGB interpolation.

    // BL (0,0): #3b82f6 (Blue-500)
    // TL (0,7): #ef4444 (Red-500)
    // TR (7,7): #eab308 (Yellow-500)
    // BR (7,0): #22c55e (Green-500)

    // Interpolate Left edge (x=0) based on y (Energy)
    // Interpolate Right edge (x=7) based on y (Energy)
    // Interpolate between Left and Right based on x (Pleasantness)

    // Simplified for MVP: return a class or hex.
    // Since we want a gradient, we might want to return a style object.

    // Let's use a predefined grid of colors or calculate it.
    // Calculating is better for gradients.

    // Red channel
    // TL(239) -- TR(234)
    // |          |
    // BL(59)  -- BR(34)

    // This is getting complex to do manually.
    // Let's use a simpler approach: 4 quadrants colors and mix them.

    const r = Math.round(
        (1 - pleasantness) * (1 - energy) * 59 +   // BL (Blue)
        (1 - pleasantness) * energy * 239 +        // TL (Red)
        pleasantness * energy * 234 +              // TR (Yellow)
        pleasantness * (1 - energy) * 34           // BR (Green)
    );

    const g = Math.round(
        (1 - pleasantness) * (1 - energy) * 130 +  // BL
        (1 - pleasantness) * energy * 68 +         // TL
        pleasantness * energy * 179 +              // TR
        pleasantness * (1 - energy) * 197          // BR
    );

    const b = Math.round(
        (1 - pleasantness) * (1 - energy) * 246 +  // BL
        (1 - pleasantness) * energy * 68 +         // TL
        pleasantness * energy * 8 +                // TR
        pleasantness * (1 - energy) * 94           // BR
    );

    return `rgb(${r}, ${g}, ${b})`;
};

export const getEmotionLabel = (x: number, y: number): string => {
    // Map coordinates to emotion labels
    // This is a simplified map. In a real app, this would be configurable.
    const emotions = [
        // Row 0 (Bottom - Low Energy)
        ["Alone", "Down", "Bored", "Tired", "Relieved", "Rested", "Tranquil", "Serene"],
        // Row 1
        ["Embarrassed", "Excluded", "Timid", "Drained", "Calm", "Relaxed", "Carefree", "Content"],
        // ... fill in others ...
        // For MVP, return a generic label if not defined
        ["Miserable", "Lonely", "Apathetic", "Weak", "Peaceful", "Thoughtful", "Balanced", "Cozy"],
        ["Disgusted", "Disappointed", "Glum", "Ashamed", "Blessed", "Comfortable", "Satisfied", "Fulfilled"],
        ["Repulsed", "Troubled", "Uneasy", "Peeved", "Pleasant", "Joyful", "Proud", "Blissful"],
        ["Fuming", "Apprehensive", "Worried", "Annoyed", "Energized", "Lively", "Optimistic", "Thrilled"],
        ["Livid", "Frightened", "Nervous", "Restless", "Hyper", "Cheerful", "Inspired", "Elated"],
        // Row 7 (Top - High Energy)
        ["Enraged", "Furious", "Frustrated", "Shocked", "Surprised", "Upbeat", "Motivated", "Ecstatic"]
    ];

    // y is row index (0 is bottom), x is col index
    return emotions[y]?.[x] || "";
};
